import React from 'react'
import { useCurrentFrame, useVideoConfig, interpolate } from 'remotion'
import {
  FilmGrain,
  Vignette,
  GradientMesh,
  AmbientParticles,
  PaperTexture,
  MetalTexture,
  NeonGlow,
  OrganicNoise,
  GlassReflection,
} from './effects'
import { COLORS } from './theme'
import { sceneLifecycle } from './animations'

/**
 * SceneWrapper — Cinematic atmosphere layer for all B-Roll scenes.
 *
 * Wraps any scene composition with:
 * 1. Animated gradient mesh background (replaces flat black)
 * 2. Ambient floating particles (depth & life)
 * 3. Subtle camera zoom (1.0 → 1.03 over duration)
 * 4. Film grain overlay (organic texture)
 * 5. Vignette (cinematic edge darkening)
 * 6. Scene lifecycle (fade in at start, fade out at end)
 *
 * Usage:
 *   <SceneWrapper accent="#FF3B30">
 *     <YourSceneContent />
 *   </SceneWrapper>
 */
/**
 * Renderiza la capa de textura de dirección de arte según el mood.
 * Cada textura encarna un carácter visual diferente.
 */
function ArtTexture({ texture, accent }) {
  if (texture === 'paper') return <PaperTexture opacity={0.06} />
  if (texture === 'metal') return <MetalTexture opacity={0.04} />
  if (texture === 'neon') return <NeonGlow accent={accent} opacity={0.12} />
  if (texture === 'organic') return <OrganicNoise opacity={0.05} />
  if (texture === 'glass') return <GlassReflection opacity={0.05} />
  // 'minimal' y 'clean' → sin textura adicional
  return null
}

export default function SceneWrapper({
  children,
  accent = COLORS.accent.blue,
  texture = null, // art direction: paper|metal|glass|organic|neon|minimal
  showParticles = true,
  grainOpacity = 0.035,
  vignetteIntensity = 0.45,
  zoomFrom = 1.0,
  zoomTo = 1.03,
  enterDur = 0.12,
  exitDur = 0.12,
}) {
  const frame = useCurrentFrame()
  const { durationInFrames } = useVideoConfig()

  // Scene lifecycle: fade in/out + scale
  const lc = sceneLifecycle(frame, durationInFrames, { enterDur, exitDur })

  // Enter: scale 0.97→1 + fade. Exit: scale 1→0.98 + fade.
  const enterScale = interpolate(lc.enter, [0, 1], [0.97, 1])
  const exitScale = interpolate(lc.exit, [0, 1], [0.98, 1]) // 1→0.98 as exit goes 1→0
  const lifecycleScale = lc.phase === 'exit' ? exitScale : enterScale

  // Camera zoom during hold phase (only applies after enter)
  const zoom = interpolate(frame, [0, durationInFrames], [zoomFrom, zoomTo], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  })

  const combinedScale = zoom * lifecycleScale

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        background: COLORS.bg,
      }}
    >
      {/* Layer 0: Animated gradient mesh background */}
      <GradientMesh accent={accent} />

      {/* Layer 1: Ambient particles (behind content) */}
      {showParticles && <AmbientParticles accent={accent} />}

      {/* Layer 2: Scene content with camera zoom + lifecycle */}
      <div
        style={{
          position: 'relative',
          zIndex: 10,
          width: '100%',
          height: '100%',
          opacity: lc.opacity,
          transform: `scale(${combinedScale})`,
          transformOrigin: 'center center',
        }}
      >
        {children}
      </div>

      {/* Layer 3: Art direction texture (mood-specific) */}
      <ArtTexture texture={texture} accent={accent} />

      {/* Layer 4: Film grain overlay */}
      <FilmGrain opacity={grainOpacity} />

      {/* Layer 5: Vignette */}
      <Vignette intensity={vignetteIntensity} />
    </div>
  )
}
